import { Component, ChangeDetectorRef, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';

import { AuthService } from '../../../core/auth/auth.service';
import { httpErrorMessage } from '../../../core/utils/http-error-message';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './login.html',
  styleUrl: './login.scss',
})
export class Login {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);

  credentials = {
    email: this.route.snapshot.queryParamMap.get('email') ?? '',
    password: '',
    remember: false,
  };

  isSubmitting = false;
  errorMessage = '';
  readonly registrationMessage = this.route.snapshot.queryParamMap.get('registered') === '1'
    ? (this.route.snapshot.queryParamMap.get('verificationSent') === '1'
        ? 'Business registered. Verify the link sent to your email, then sign in to complete onboarding.'
        : 'Business registered. Verify your email, then sign in to complete onboarding.')
    : this.route.snapshot.queryParamMap.get('invited') === '1'
      ? 'Your team account is ready. Sign in with the temporary password from your invitation email, then create your own password.'
      : this.route.snapshot.queryParamMap.get('reset') === '1'
        ? 'Password updated successfully. Sign in with your new password.'
        : '';

  onSubmit(): void {
    if (this.isSubmitting) {
      return;
    }

    this.errorMessage = '';

    const emailOrUserName = this.credentials.email.trim();
    const password = this.credentials.password.trim();

    if (!emailOrUserName || !password) {
      this.errorMessage = 'Email and password are required.';
      this.cdr.detectChanges();
      return;
    }

    this.isSubmitting = true;
    this.cdr.detectChanges();

    console.time('Login API');

    this.auth
      .login({
        emailOrUserName,
        password,
        ipAddress: null,
        userAgent: navigator.userAgent,
      })
      .pipe(
        finalize(() => {
          console.timeEnd('Login API');
          this.isSubmitting = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response) => {
          const destination = response.user.mustChangePassword
            ? '/change-password'
            : this.auth.businessLandingRoute();
          void this.router.navigate([destination]).then((navigated) => {
            if (!navigated) {
              this.errorMessage = 'Signed in, but the assigned workspace could not be opened. Ask the owner to verify your role and location access.';
              this.cdr.detectChanges();
            }
          }).catch(() => {
            this.errorMessage = 'Signed in, but the assigned workspace could not be opened.';
            this.cdr.detectChanges();
          });
        },
        error: (error: unknown) => {
          console.log('Login Error:', error);

          this.errorMessage = httpErrorMessage(
            error,
            'Unable to sign in. Check your credentials.'
          );

          this.cdr.detectChanges();
        },
      });
  }
}
